import { Component, Input, OnInit, inject } from "@angular/core";
import { Pages } from "@constants/pages/Pages";
import { TabService } from "@services/tabs/TabService";
import { ControlDataService } from "../services/control.data.service";
import { ItemLinkableBase } from "@app/shared/linkable/item-linkable-base";
import { Observable } from "rxjs";
import { IHasGuid } from "@app/shared/interfaces/IHasGuid";
import { RendererConfig } from "@models/workspace/RendererConfig";
import { RendererType } from "@enums/RendererType";
import { LinkableIconConfig } from "@app/shared/models/Linkable/LinkableIconConfig";
import { nameof } from "@methods/jeffs-toolkit";
import { ControlLinkModel } from "@models/controls/ControlLinkModel";
import { ControlConfigModel } from "../models/ControlConfigModel";
import { ModuleType } from "@enums/ModuleType";
import { SearchKeyword } from "@models/workspace/SearchKeyword";
import { FilterType } from "@enums/FilterType";
import { FilterConfig } from "@models/workspace/FilterConfig";
import { TranslatePipe, injectTranslation, provideTranslationScope } from "@app/shared/i18n/translation-scope";
import { scope } from "@app/controls/i18n/scope";
import { LinkableComponent } from "@app/shared/linkable/linkable.component";

@Component({
    selector: "control-linkable",
    templateUrl: "./control-linkable.component.html",
    styleUrls: ["./control-linkable.component.scss"],
    imports: [LinkableComponent, TranslatePipe],
    viewProviders: [provideTranslationScope(scope)],
})
export class ControlLinkableComponent extends ItemLinkableBase implements OnInit {
    @Input() moduleType = ModuleType.Controls;
    @Input() parentModuleType: ModuleType;
    @Input() parentId: number;
    @Input() parentOrganizationId: number;
    @Input() parentBusinessDimensions: number[];
    @Input() parentFrameworkDimensions: number[];
    @Input() activeFilters: SearchKeyword[] = [];
    @Input() showAdvancedFilters = false;

    @Input() hidden: boolean;

    hasAccessChecks = {};
    accessCheckInProgress = false;

    filterConfig: FilterConfig[] = [
        {
            label: "Organizations",
            column: nameof<ControlLinkModel>((x) => x.organizationId),
            type: FilterType.Organization,
            hideActionColumn: true,
            param: "",
        },
        {
            label: "Business Dimensions",
            column: nameof<ControlLinkModel>((x) => x.businessDimensionIds),
            type: FilterType.BusinessDimensions,
            hideActionColumn: true,
            param: "",
        },
        {
            label: "Framework Dimensions",
            column: nameof<ControlLinkModel>((x) => x.frameworkDimensionIds),
            type: FilterType.FrameworkDimensions,
            hideActionColumn: true,
            param: "",
        },
    ];

    rowIconConfig: LinkableIconConfig[] = [
        {
            icon: "fas fa-key",
            actionColumn: nameof<ControlLinkModel>((c) => c.isKeyControl),
            actionValue: true,
        },
        {
            icon: "fas fa-share-alt",
            actionColumn: nameof<ControlLinkModel>((c) => c.isShared),
            actionValue: true,
        },
    ];

    rendererConfig: RendererConfig[] = [
        {
            type: RendererType.Score,
            textColumn: "Conclusion score",
            actionColumn: "Conclusion score color",
            hideActionColumn: true,
        },
    ];

    private _controlDS = inject(ControlDataService);
    private _pages = inject(Pages);
    private _tabService = inject(TabService);
    private _translation = injectTranslation(scope);

    async ngOnInit(): Promise<void> {
        await this._translation.ready();
        this.filterConfig[0].label = this._translation.t("linkable.filterOrganizations");
        this.filterConfig[1].label = this._translation.t("linkable.filterBusinessDimensions");
        this.filterConfig[2].label = this._translation.t("linkable.filterFrameworkDimensions");
    }

    hasAccessToItem(guid: string): Observable<boolean> {
        return this._controlDS.hasAccess(guid);
    }

    openTab(item: IHasGuid, hasAccess: boolean) {
        if (hasAccess === true) {
            this._tabService.generateTab(
                this._pages.ControlDetail,
                item.guid,
                `(C) ${item[this.displayValueProp]}`,
            );
        } else {
            this._toastr.warning(this._translation.t("linkable.notEnoughRights"));
        }
    }

    addClicked() {
        const config = <ControlConfigModel>{};

        if (this.parentModuleType == ModuleType.ORM) {
            config.riskId = this.parentId;
            config.organizationId = this.parentOrganizationId;
            config.businessIds = this.parentBusinessDimensions;
            config.frameworkIds = this.parentFrameworkDimensions;
        }

        this._tabService.generateTab(this._pages.ControlDetail, null, this._translation.t("linkable.addNewControl"), config);
    }
}
